import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import './styles/app.css';
import './i18n/index.ts';

import { App } from './App.tsx';
import { createQueryClient } from './api/queryClient.ts';
import { readDateTimePreference } from './format/dateTimePreference.ts';
import { DateTimeFormatProvider } from './format/DateTimeFormatProvider.tsx';
import { readPreference } from './theme/preference.ts';
import { ThemeProvider } from './theme/ThemeProvider.tsx';

/**
 * Entry point, and the only place in the app that reads ambient browser state during startup.
 *
 * `readPreference` touches `localStorage`, which is impure; doing it here rather than in a lazy
 * `useState` initialiser keeps every component's render a pure function of its props and state,
 * which is what the `react-hooks` purity rules in `eslint.config.js` are checking for.
 *
 * `./i18n/index.ts` reads `navigator.languages` at module load too, for the same reason — its
 * `init()` call needs to run, synchronously, before any component calls `useTranslation`. Unlike
 * the two preferences above it has no `initial*` prop to thread through, since `react-i18next`'s
 * `initReactI18next` plugin registers against the module-level `i18next` singleton directly rather
 * than through a value this tree passes down.
 */
const container = document.getElementById('root');
if (container === null) {
  throw new Error('#root is missing from index.html');
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={createQueryClient()}>
      <ThemeProvider initialPreference={readPreference(window.localStorage)}>
        <DateTimeFormatProvider initialPreference={readDateTimePreference(window.localStorage)}>
          <App />
        </DateTimeFormatProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
